import Link from "next/link";
import {
  Facebook,
  Instagram,
} from "lucide-react";
import type { SocialMediaLink } from "@/lib/footer-utils";

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z" />
    </svg>
  );
}

/**
 * Reusable Social Media Icons Component
 * 
 * Renders social media links with consistent styling and icons.
 * Can be used in footer, header, or anywhere else.
 */

interface SocialMediaIconsProps {
  links?: SocialMediaLink[];
  className?: string;
  iconClassName?: string;
  showLabels?: boolean;
}

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: LinkedInIcon,
} as const;

const platformLabels = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
} as const;

export function SocialMediaIcons({ 
  links, 
  className = "flex space-x-3",
  iconClassName = "text-white/80 hover:text-white transition-colors",
  showLabels = false
}: SocialMediaIconsProps) {
  if (!links || links.length === 0) return null;

  return (
    <div className={className}>
      {links.map((link) => {
        const Icon = platformIcons[link.platform];
        const label = platformLabels[link.platform];

        return (
          <Link
            key={link.platform}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={iconClassName}
            aria-label={label}
          >
            <Icon className="w-5 h-5" />
            {showLabels && (
              <span className="ml-2">{label}</span>
            )}
          </Link>
        );
      })}
    </div>
  );
}